<template>
  <div style="height:100%">
    <div>
      <v-row no-gutters>
        <v-col cols="12" md="auto">
          <v-row>
            <v-col cols="auto" style="width:270px" class="pb-0 ml-4">
              <v-select
                :label="$t('County')"
                :disabled="!countyEnabled"
                v-model="county"
                :items="availableCounties"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="auto" style="width:270px" class="pb-0">
              <v-autocomplete v-model="city" :items="availableCities" :label="$t('Settlement')" clearable></v-autocomplete>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="auto">
          <v-row>
            <v-col cols="auto" class="ml-4">
              <v-checkbox :label="$t('By primary address')" v-model="byPrimaryAddress"></v-checkbox>
            </v-col>
            <v-col cols="auto" class="d-flex align-center mb-3">
              <v-btn
                @click="refresh()"
                :loading="isLoading"
                :disabled="isLoading"
                >{{$t('Refresh')}}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <div id="plotly" v-resize:debounce.100="onResize"></div>
  </div>
</template>

<script>
import plotly from 'plotly.js/dist/plotly-basic.min'
import api from "../services/ApiService";
import { mapState } from "vuex";
import { plotlyRegisterConfig } from "../helpers/chart/plotly";
import { translateLabel } from "../helpers/dataTranslations";

export default {
  name: "ChartAges",

  data() {
    return {
      county: undefined,
      countyEnabled: true,
      availableCounties: [],
      availableCities: [],
      city: undefined,
      cityEnabled: true,
      byPrimaryAddress: false,
      isLoading: false
    };
  },

  computed: {
    ...mapState({
      statuses: state => state.app.config.statuses
    })
  },

  watch: {
    async county(selectedCounty) {
      const { data: cities } = await api.getChartCities(selectedCounty);
      this.availableCities = cities;
      this.city = "";
    },
    "$i18n.locale"() {
      this.refresh();
    }
  },

  async mounted() {
    plotly.register(plotlyRegisterConfig);
    const { data: counties } = await api.getChartCounties();
    this.availableCounties = counties;
    this.county = "";

    this.refresh();
  },

  methods: {
    async refresh() {
      this.isLoading = true;
      const bucketSize = 5;

      const yValues = ["n/a"];
      for (let i = 0; i < 100; i += bucketSize) {
        yValues.push(`${i === 0 ? i : i + 1}-${i + bucketSize}`);
      }
      yValues.push('100+');

      const { data: result } = await api.getChartAgesStats({
        bucketSize,
        county: this.county,
        city: this.city,
        byPrimaryAddress: this.byPrimaryAddress
      });

      const usedStatuses = ["elhunyt", "beteg", "gyogyult"];

      let data = [];

      const genders = [
        { name: "male", showLegend: true, multiplier: -1 },
        { name: "female", showLegend: false, multiplier: 1 }
      ];

      genders.forEach(gender => {
        usedStatuses.forEach(value => {
          const status = translateLabel(this.statuses).find(s => s.value === value);

          const trace = {
            text: [],
            y: yValues,
            x: [],
            name: status.label,
            type: "bar",
            marker: {
              color: status.color,
              opacity: 0.7
            },
            orientation: "h",
            showlegend: gender.showLegend,
            hoverinfo: "text"
          };

          yValues.forEach(x => {
            const item = result.find(
              v =>
                v.gender === gender.name &&
                v.status === status.value &&
                v.ageRange === x
            );
            trace.text.push(status.label + ": " + (item ? item.count : 0));
            trace.x.push(item ? item.count * gender.multiplier : 0);
          });

          data.push(trace);
        });
      });

      // calculate the range for x axis
      let maxCount = 0;
      for (let i = 0; i < data[0].x.length; i++) {
        let maleCount = 0;
        let femaleCount = 0;
        for (let j = 0; j < 6; j++) {
          maleCount += data[j].x[i] < 0 ? data[j].x[i] * -1 : 0;
          femaleCount += data[j].x[i] > 0 ? data[j].x[i] : 0;
        }
        if (maleCount > maxCount) {
          maxCount = maleCount;
        }
        if (femaleCount > maxCount) {
          maxCount = femaleCount;
        }
      }

      let title = this.$t("Age pyramid");
      if (this.county) {
        title += ", " + this.county;
      }
      if (this.city) {
        title += ", " + this.city;
      }
      if (this.byPrimaryAddress) {
        title += ", " + this.$t('By primary address').toLowerCase();
      }

      if (maxCount < 10) {
        maxCount = 10;
      }

      const step = Math.ceil(maxCount / 10);

      let currentTick = 0;

      let offset = 10;
      let tmp = [];
      while (currentTick <= maxCount + offset) {
        tmp.push(currentTick);
        currentTick += step;
      }

      let reverse = [...tmp].reverse();

      let ticktext = [...reverse, ...tmp];
      let tickvals = [...reverse.map(x => x * -1), ...tmp];

      let layout = {
        xaxis: {
          type: "linear",
          range: [(maxCount + offset) * -1, maxCount + offset],
          tickvals: tickvals,
          ticktext: ticktext
        },
        bargap: 0,
        barmode: "relative",
        autosize: true,
        title: {
          text: title,
          xref: "paper",
          x: 0.05
        },
        legend: {
          orientation: "h"
        },
        annotations: [
          {
            xref: "paper",
            yref: "paper",
            x: 0.25,
            y: 1.05,
            text: this.$t("Men"),
            showarrow: false
          },
          {
            xref: "paper",
            yref: "paper",
            x: 0.75,
            y: 1.05,
            text: this.$t("Women"),
            showarrow: false
          }
        ]
      };

      plotly.newPlot("plotly", data, layout, {
        locale:this.$i18n.locale,
        displaylogo: false,
        showTips: false,
        displayModeBar: true,
        toImageButtonOptions: {
          format: "png",
          filename: this.$t("Age pyramid").toLowerCase(),
          height: 768,
          width: 1024,
          scale: 1
        }
      });

      this.isLoading = false;
    },

    onResize() {
      plotly.Plots.resize("plotly");
    }
  }
};
</script>

<style>
.js-plotly-plot .plotly .modebar {
  right: 20px;
}
#plotly {
  height: calc(100vh - 250px);
  overflow: hidden;
}

@media only screen and (max-width: 960px) {
  #plotly {
    height: calc(100vh - 350px);
  }
}

.plotly {
  height: 100%;
}

.dateSelector {
  width: 170px;
}
</style>
